import React from 'react';
import { useNavigate } from 'react-router-dom';

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#09090e] text-[#f0eff6]">
      {/* Top Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-8 py-6 bg-[#09090e]/80 backdrop-blur-md border-b border-[#22222e]">
        <div className="flex items-center gap-3">
          <span className="text-[#ff4500] text-2xl">⬤</span>
          <span className="font-bold text-xl" style={{ fontFamily: 'Syne, sans-serif' }}>PainScout</span>
        </div>
        <button 
          onClick={() => navigate('/dashboard')}
          className="px-6 py-2.5 border-2 border-[#ff4500] text-[#ff4500] rounded-lg hover:bg-[#ff4500] hover:text-white transition-all duration-300 font-medium"
          data-testid="enter-dashboard-nav-btn"
        >
          Enter Dashboard →
        </button>
      </nav>

      {/* Hero Section */}
      <div className="flex flex-col items-center justify-center min-h-screen px-8 pt-20 fade-in">
        <p className="text-[#7b7a88] text-sm mb-4 tracking-wider" style={{ fontFamily: 'DM Mono, monospace' }}>
          POWERED BY AMAZON NOVA AI
        </p>
        <h1 
          className="text-6xl md:text-7xl lg:text-8xl font-bold text-center leading-tight mb-6 max-w-6xl"
          style={{ fontFamily: 'Syne, sans-serif' }}
          data-testid="hero-headline"
        >
          Every Reddit complaint<br />is a billion dollar idea.
        </h1>
        <p className="text-[#7b7a88] text-base md:text-lg text-center max-w-3xl mb-10" style={{ fontFamily: 'DM Mono, monospace' }}>
          PainScout monitors 26 communities in real-time,<br />
          uses Nova AI to identify solvable problems,<br />
          and generates full market intelligence — automatically.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="px-8 py-4 bg-[#ff4500] text-white rounded-lg hover:bg-[#e03d00] transition-all duration-300 font-semibold text-lg"
            data-testid="enter-dashboard-hero-btn"
          >
            → Enter Dashboard
          </button>
          <button 
            onClick={() => navigate('/vibe-coder')}
            className="px-8 py-4 bg-[#4ade80] text-[#09090e] rounded-lg hover:bg-[#3bc46e] transition-all duration-300 font-semibold text-lg flex items-center gap-2"
            data-testid="vibe-coder-btn"
          >
            <span>✨</span>
            <span>Try Vibe Coder</span>
          </button>
          <button className="px-8 py-4 border-2 border-[#7b7a88] text-[#7b7a88] rounded-lg hover:border-white hover:text-white transition-all duration-300 font-semibold text-lg">
            Watch Demo ↗
          </button>
        </div>
      </div>

      {/* Animated Ticker */}
      <div className="overflow-hidden border-y border-[#22222e] bg-[#17171c] py-4">
        <div className="ticker-animation flex gap-8 whitespace-nowrap" style={{ fontFamily: 'DM Mono, monospace' }}>
          <span className="text-[#7b7a88]">r/personalfinance</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/ADHD</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/techsupport</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/sleep</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/careerguidance</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/mentalhealth</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/loseit</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/relationship_advice</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/personalfinance</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/ADHD</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/techsupport</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/sleep</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/careerguidance</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/mentalhealth</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/loseit</span>
          <span className="text-[#ff4500]">•</span>
          <span className="text-[#7b7a88]">r/relationship_advice</span>
          <span className="text-[#ff4500]">•</span>
        </div>
      </div>

      {/* Stats Row */}
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-8 text-center hover:border-[#ff4500] transition-all duration-300" data-testid="stat-subreddits">
            <div className="text-5xl font-bold text-[#ff4500] mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>26</div>
            <div className="text-[#7b7a88] text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>Subreddits Monitored</div>
          </div>
          <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-8 text-center hover:border-[#ff4500] transition-all duration-300" data-testid="stat-posts">
            <div className="text-5xl font-bold text-[#ff4500] mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>~1.7M</div>
            <div className="text-[#7b7a88] text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>Posts/Day Analyzed</div>
          </div>
          <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-8 text-center hover:border-[#ff4500] transition-all duration-300" data-testid="stat-powered">
            <div className="text-5xl font-bold text-[#ff4500] mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>Nova</div>
            <div className="text-[#7b7a88] text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>Powered by Amazon Nova Pro</div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="max-w-7xl mx-auto px-8 py-20">
        <h2 className="text-4xl font-bold text-center mb-16" style={{ fontFamily: 'Syne, sans-serif' }}>How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="relative" data-testid="how-it-works-step-1">
            <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[#ff4500] text-2xl">⬤</span>
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>Listen</h3>
              </div>
              <p className="text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
                Real-time stream from 26 subreddits
              </p>
            </div>
            <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-[2px] bg-gradient-to-r from-[#ff4500] to-transparent"></div>
          </div>
          <div className="relative" data-testid="how-it-works-step-2">
            <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[#ff4500] text-2xl">⬤</span>
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>Classify</h3>
              </div>
              <p className="text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
                Nova AI filters real problems
              </p>
            </div>
            <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-[2px] bg-gradient-to-r from-[#ff4500] to-transparent"></div>
          </div>
          <div className="relative" data-testid="how-it-works-step-3">
            <div className="bg-[#17171c] border border-[#22222e] rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[#ff4500] text-2xl">⬤</span>
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>Research</h3>
              </div>
              <p className="text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
                Full market intelligence generated
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#22222e] py-8 text-center text-[#7b7a88] text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>
        Built for the Amazon Nova Hackathon 2024
      </footer>
    </div>
  );
};

export default Landing;
